import { useEffect, useState } from 'react';
import { colour, radius, space, text } from '../design/tokens.ts';
import { Button, Card, Field, TextInput } from '../components/basics.tsx';
import { MaestroMark } from '../app/MenuBar.tsx';
import { api, chooseNewPassword, session, signIn } from '../api/client.ts';

/**
 * Sign-in, on Maestro's own page (docs/06, decision 2): no hosted UI, no OAuth
 * redirect. The browser posts an email and a password, and the server does the
 * talking to Cognito — Maestro's own front end never holds a client secret
 * because there is not one.
 *
 * An admin-created person arrives with a temporary password and is asked to
 * choose their own, which is the `NEW_PASSWORD_REQUIRED` branch.
 *
 * In local development the server runs with `AUTH_MODE=dev` and answers 501
 * here. Rather than leave a dead form, this offers the sample people — it is
 * the only way to try the roles locally, and it says plainly what it is.
 */
export function SignIn({ onSignedIn }: { onSignedIn: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const [challenge, setChallenge] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [why, setWhy] = useState<string | null>(null);
  const [devMode, setDevMode] = useState<boolean | null>(null);

  // Ask the server which mode it is in, so the page offers the right thing.
  useEffect(() => {
    void api
      .get<{ auth?: { mode?: string } }>('/api/health')
      .then((health) => setDevMode(health.auth?.mode === 'dev'))
      .catch(() => setDevMode(null));
  }, []);

  const submit = async (): Promise<void> => {
    setBusy(true);
    setWhy(null);
    if (challenge) {
      if (password !== again) {
        setWhy('Those two passwords are not the same.');
        setBusy(false);
        return;
      }
      const out = await chooseNewPassword(email, password, challenge);
      setBusy(false);
      if (out.ok) onSignedIn();
      else setWhy(out.why);
      return;
    }
    const out = await signIn(email, password);
    setBusy(false);
    if (out.ok) {
      onSignedIn();
      return;
    }
    if ('newPasswordRequired' in out) {
      setChallenge(out.session);
      setPassword('');
      setWhy('This account needs a password of your own. Choose one now.');
      return;
    }
    setWhy(out.why);
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        padding: '40px 16px',
        background: colour.ground
      }}
    >
      <div style={{ width: 'min(100%, 440px)', display: 'flex', flexDirection: 'column', gap: space.section }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <MaestroMark size={64} />
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ font: "600 24px/30px 'IBM Plex Sans', system-ui, sans-serif" }}>Maestro</span>
            <span style={{ ...text.note, color: colour.inkMuted }}>
              What is the latest known good, and where is it?
            </span>
          </div>
        </div>

        <Card>
          <h1 style={{ margin: 0, ...text.h2 }}>
            {challenge ? 'Choose your password' : 'Sign in with your Arcturus account.'}
          </h1>
          <span style={{ ...text.small, color: colour.inkSoft }}>
            Sign-in is handled by AWS Cognito. Maestro never sees your password.
          </span>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
            style={{ display: 'flex', flexDirection: 'column', gap: space.control, marginTop: space.tight }}
          >
            <Field label="Email">
              <TextInput value={email} onChange={setEmail} type="email" autoComplete="username" />
            </Field>
            <Field label={challenge ? 'New password' : 'Password'}>
              <TextInput
                value={password}
                onChange={setPassword}
                type="password"
                autoComplete={challenge ? 'new-password' : 'current-password'}
              />
            </Field>
            {challenge && (
              <Field label="New password again">
                <TextInput value={again} onChange={setAgain} type="password" autoComplete="new-password" />
              </Field>
            )}
            {why && (
              <span
                style={{
                  padding: '10px 12px',
                  borderRadius: radius.control,
                  background: colour.staleWash,
                  border: `1px solid ${colour.stale}`,
                  ...text.small,
                  color: colour.staleInk
                }}
              >
                {why}
              </span>
            )}
            <Button tone="primary" type="submit" disabled={busy || email === '' || password === ''}>
              {busy ? 'Signing in…' : challenge ? 'Set it and sign in' : 'Sign in'}
            </Button>
          </form>
        </Card>

        {devMode && (
          <Card title="Local development">
            <span style={{ ...text.small, color: colour.inkSoft }}>
              This server is running with <code style={text.mono}>AUTH_MODE=dev</code>, so there is no
              Cognito pool to sign in to and the form above will answer 501. Pick somebody from the
              sample data instead. Production refuses to start in this mode.
            </span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.tight }}>
              {[
                { sub: 'sample-ewan', says: 'Ewan · admin, can mark good' },
                { sub: 'sample-brian', says: 'Brian · operator' },
                { sub: 'sample-kcross', says: 'Kcross · operator' },
                { sub: 'sample-viewer', says: 'A viewer · read only' }
              ].map((who) => (
                <Button
                  key={who.sub}
                  onClick={() => {
                    session.holdDevUser(who.sub);
                    onSignedIn();
                  }}
                >
                  {who.says}
                </Button>
              ))}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
